import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Download, Table2, Search, ChevronLeft, ChevronRight, Split, Loader2, RefreshCw, CheckSquare, Square, Zap, Filter, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', PKR: 'Rs', INR: '₹', AED: 'AED',
  JPY: '¥', CNY: '¥', CAD: 'C$', AUD: 'A$', SAR: 'SAR', BRL: 'R$',
};

export const ProcessedData = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassifyTaxonomy, setReclassifyTaxonomy] = useState('custom');
  const [reclassifyTaxonomyId, setReclassifyTaxonomyId] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [taxonomies, setTaxonomies] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkReclassifying, setBulkReclassifying] = useState(false);
  const [bulkTaxonomy, setBulkTaxonomy] = useState('custom');
  const [bulkTaxonomyId, setBulkTaxonomyId] = useState('');
  const [filterL1, setFilterL1] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterConfMin, setFilterConfMin] = useState('');
  const [filterConfMax, setFilterConfMax] = useState('');
  const [filterCash, setFilterCash] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [meta, setMeta] = useState({ unique_l1: [], unique_suppliers: [], cash_count: 0, has_l2: false, has_unspsc: false, has_original_cat: false, has_splits: false, extra_columns: [], incomplete_count: 0 });
  const [reclassifyingIncomplete, setReclassifyingIncomplete] = useState(false);
  const perPage = 50;
  const searchTimeout = useRef(null);

  useEffect(() => {
    fetchSessions();
    fetchTaxonomies();
  }, []);

  const fetchTaxonomies = async () => {
    try {
      const res = await axios.get(`${API}/taxonomy`, { headers: { Authorization: `Bearer ${token}` } });
      setTaxonomies(res.data.taxonomies || []);
    } catch { /* ignore */ }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/spend/processing-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const classified = res.data.sessions.filter(s => s.status === 'classified');
      setSessions(classified);
      if (classified.length > 0) {
        setSelectedSession(classified[0]);
        fetchMeta(classified[0].id);
        fetchRecords(classified[0].id, 1, '', '', '', '', '', '');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeta = async (uploadId) => {
    try {
      const res = await axios.get(`${API}/spend/records-meta?upload_id=${uploadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeta(res.data);
    } catch { /* ignore */ }
  };

  const fetchRecords = useCallback(async (uploadId, pg, search, fL1, fSupplier, fConfMin, fConfMax, fCash) => {
    setRecordsLoading(true);
    try {
      const params = new URLSearchParams({ upload_id: uploadId, page: pg, per_page: perPage });
      if (search) params.set('search', search);
      if (fL1) params.set('filter_l1', fL1);
      if (fSupplier) params.set('filter_supplier', fSupplier);
      if (fConfMin) params.set('filter_conf_min', fConfMin);
      if (fConfMax) params.set('filter_conf_max', fConfMax);
      if (fCash) params.set('filter_cash', fCash);
      const res = await axios.get(`${API}/spend/records?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(res.data.records || []);
      setTotalRecords(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load records');
    } finally {
      setRecordsLoading(false);
    }
  }, [token]);

  const selectSession = (session) => {
    setSelectedSession(session);
    setReclassifyTaxonomy(session?.category_mode === 'keep_existing' ? 'keep_existing' : 'custom');
    setRecords([]);
    setPage(1);
    setSearchTerm('');
    setExpandedRow(null);
    setSelectedIds(new Set());
    setFilterL1(''); setFilterSupplier(''); setFilterConfMin(''); setFilterConfMax(''); setFilterCash('');
    fetchMeta(session.id);
    fetchRecords(session.id, 1, '', '', '', '', '', '');
  };

  const refreshCurrentPage = () => {
    if (!selectedSession) return;
    fetchRecords(selectedSession.id, page, searchTerm, filterL1, filterSupplier, filterConfMin, filterConfMax, filterCash);
  };

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    setPage(1);
    setSelectedIds(new Set());
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      if (selectedSession) fetchRecords(selectedSession.id, 1, val, filterL1, filterSupplier, filterConfMin, filterConfMax, filterCash);
    }, 400);
  };

  const handleFilterChange = (setter, value, which) => {
    setter(value);
    setPage(1);
    setSelectedIds(new Set());
    const newFilters = { l1: filterL1, supplier: filterSupplier, confMin: filterConfMin, confMax: filterConfMax, cash: filterCash };
    newFilters[which] = value;
    if (selectedSession) fetchRecords(selectedSession.id, 1, searchTerm, newFilters.l1, newFilters.supplier, newFilters.confMin, newFilters.confMax, newFilters.cash);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setExpandedRow(null);
    if (selectedSession) fetchRecords(selectedSession.id, newPage, searchTerm, filterL1, filterSupplier, filterConfMin, filterConfMax, filterCash);
  };

  const handleReclassifyIncomplete = async () => {
    if (!selectedSession) return;
    setReclassifyingIncomplete(true);
    try {
      const res = await axios.post(
        `${API}/spend/reclassify-incomplete/${selectedSession.id}?taxonomy=${reclassifyTaxonomy === 'keep_existing' ? 'custom' : reclassifyTaxonomy}${reclassifyTaxonomyId ? `&taxonomy_id=${reclassifyTaxonomyId}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.async) {
        toast.success(`Reclassifying ${res.data.total_incomplete} incomplete records in background. Check progress on Processing Results page.`);
      } else if (res.data.records_reclassified > 0) {
        toast.success(`Reclassified ${res.data.records_reclassified} records (avg. confidence: ${(res.data.average_confidence * 100).toFixed(0)}%)`);
        refreshCurrentPage();
        fetchMeta(selectedSession.id);
      } else {
        toast.info('No incomplete records found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reclassification failed');
    } finally {
      setReclassifyingIncomplete(false);
    }
  };

  const handleExport = async () => {
    if (!selectedSession) return;
    try {
      const res = await axios.get(`${API}/spend/export/${selectedSession.id}?format=csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cleansed_data_${selectedSession.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data exported successfully');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleReclassify = async () => {
    if (!selectedSession) return;
    setReclassifying(true);
    try {
      let url = `${API}/spend/classify/${selectedSession.id}?taxonomy=${reclassifyTaxonomy}`;
      if (reclassifyTaxonomyId) url += `&taxonomy_id=${reclassifyTaxonomyId}`;
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Classification started! Refreshing data...');
      const res = await axios.get(`${API}/spend/processing-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const classified = res.data.sessions.filter(s => s.status === 'classified');
      setSessions(classified);
      const updated = classified.find(s => s.id === selectedSession.id);
      if (updated) selectSession(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Classification failed');
    } finally {
      setReclassifying(false);
    }
  };

  const handleBulkReclassify = async () => {
    if (selectedIds.size === 0) { toast.error('Select records first'); return; }
    setBulkReclassifying(true);
    try {
      const payload = {
        record_ids: [...selectedIds],
        taxonomy: bulkTaxonomy,
      };
      if (bulkTaxonomyId) payload.taxonomy_id = bulkTaxonomyId;
      const res = await axios.post(`${API}/spend/bulk-reclassify`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Reclassified ${res.data.records_reclassified} records (avg. confidence: ${(res.data.average_confidence * 100).toFixed(0)}%)`);
      setSelectedIds(new Set());
      if (selectedSession) refreshCurrentPage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk reclassification failed');
    } finally {
      setBulkReclassifying(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === records.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(records.map(r => r.id)));
    }
  };

  const TaxonomySelect = ({ value, onChange, taxonomyId, onTaxonomyIdChange, testIdPrefix }) => (
    <div className="flex items-center gap-2">
      <select value={value} onChange={e => { onChange(e.target.value); onTaxonomyIdChange(''); }}
        className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs bg-white"
        data-testid={`${testIdPrefix}-taxonomy-type`}>
        <option value="custom">AI Generated L1-L4</option>
        <option value="unspsc">UNSPSC L1-L4</option>
        <option value="reclassify_unspsc">Reclassify to UNSPSC</option>
        <option value="bespoke">Bespoke Taxonomy</option>
        <option value="keep_existing">Keep my categories</option>
      </select>
      {value === 'bespoke' && (
        taxonomies.length > 0 ? (
          <select value={taxonomyId} onChange={e => onTaxonomyIdChange(e.target.value)}
            className="px-2 py-1.5 border border-blue-300 rounded-sm text-xs bg-white"
            data-testid={`${testIdPrefix}-taxonomy-id`}>
            <option value="">Select taxonomy...</option>
            {taxonomies.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        ) : (
          <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-sm border border-amber-200" data-testid={`${testIdPrefix}-no-taxonomy-msg`}>
            No bespoke taxonomies — create one in Category Taxonomy first
          </span>
        )
      )}
    </div>
  );

  // Use server-side metadata for filter dropdowns
  const uniqueL1 = meta.unique_l1 || [];
  const uniqueSuppliers = meta.unique_suppliers || [];
  const cashCount = meta.cash_count || 0;
  const activeFilterCount = [filterL1, filterSupplier, filterConfMin, filterConfMax, filterCash].filter(Boolean).length;

  const clearFilters = () => {
    setFilterL1(''); setFilterSupplier(''); setFilterConfMin(''); setFilterConfMax(''); setFilterCash('');
    setPage(1);
    setSelectedIds(new Set());
    if (selectedSession) fetchRecords(selectedSession.id, 1, searchTerm, '', '', '', '', '');
  };

  const totalPages = Math.ceil(totalRecords / perPage);

  const hasL2 = meta.has_l2;
  const hasOriginalCat = meta.has_original_cat;
  const hasUnspsc = meta.has_unspsc;
  const extraCols = meta.extra_columns || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Table2 className="mx-auto mb-4 text-slate-300" size={48} />
          <p className="text-slate-600">No processed data available. Upload and classify data first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="processed-data-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-1">Processed Data</h1>
          <p className="text-slate-600 text-sm">Review your cleansed and classified dataset</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleExport}
            disabled={!selectedSession || totalRecords === 0}
            className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-2"
            data-testid="export-csv-button"
          >
            <Download size={16} />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Controls Row */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-700">Session:</label>
          <select
            data-testid="data-session-selector"
            value={selectedSession?.id || ''}
            onChange={(e) => {
              const s = sessions.find(s => s.id === e.target.value);
              if (s) selectSession(s);
            }}
            className="px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {sessions.map(s => (
              <option key={s.id} value={s.id}>{s.filename} ({s.total_records} records)</option>
            ))}
          </select>
        </div>

        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="data-search-input"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search supplier, description, category..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <span className="text-xs text-slate-500 ml-auto">
          {recordsLoading ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
          {totalRecords} records
        </span>
      </div>

      {/* Filter Controls */}
      {totalRecords > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(!showFilters)}
              className={'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ' +
                (activeFilterCount > 0 ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')}
              data-testid="toggle-filters-btn">
              <Filter size={13} />
              Filters {activeFilterCount > 0 && <span className="bg-indigo-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="clear-filters-btn">
                <X size={12} /> Clear filters
              </button>
            )}
          </div>
          {showFilters && (
            <div className="flex flex-wrap items-end gap-3 p-3 bg-slate-50 border border-slate-200 rounded-sm" data-testid="filter-panel">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Category (L1)</label>
                <select value={filterL1} onChange={e => handleFilterChange(setFilterL1, e.target.value, 'l1')}
                  className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs bg-white min-w-[140px]"
                  data-testid="filter-l1-select">
                  <option value="">All categories</option>
                  {uniqueL1.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Supplier</label>
                <select value={filterSupplier} onChange={e => handleFilterChange(setFilterSupplier, e.target.value, 'supplier')}
                  className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs bg-white min-w-[140px]"
                  data-testid="filter-supplier-select">
                  <option value="">All suppliers</option>
                  {uniqueSuppliers.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Confidence (%)</label>
                <div className="flex items-center gap-1">
                  <input value={filterConfMin} onChange={e => handleFilterChange(setFilterConfMin, e.target.value, 'confMin')}
                    type="number" min="0" max="100" placeholder="Min"
                    className="w-16 px-2 py-1.5 border border-slate-200 rounded-sm text-xs bg-white"
                    data-testid="filter-conf-min" />
                  <span className="text-xs text-slate-400">—</span>
                  <input value={filterConfMax} onChange={e => handleFilterChange(setFilterConfMax, e.target.value, 'confMax')}
                    type="number" min="0" max="100" placeholder="Max"
                    className="w-16 px-2 py-1.5 border border-slate-200 rounded-sm text-xs bg-white"
                    data-testid="filter-conf-max" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Payment Type</label>
                <select value={filterCash} onChange={e => handleFilterChange(setFilterCash, e.target.value, 'cash')}
                  className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs bg-white min-w-[100px]"
                  data-testid="filter-cash-select">
                  <option value="">All</option>
                  <option value="cash">Cash Only ({cashCount})</option>
                  <option value="non-cash">Non-Cash</option>
                </select>
              </div>
              <Button size="sm" variant="outline" onClick={() => { setSelectedIds(new Set(records.map(r => r.id))); }}
                className="text-xs gap-1 h-7" data-testid="select-filtered-btn">
                <CheckSquare size={12} /> Select page ({records.length})
              </Button>
            </div>
          )}
        </div>
      )}

      {/* On-Demand Classify / Reclassify */}
      {totalRecords > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="classify-bar">
          <Zap size={16} className="text-blue-600 flex-shrink-0" />
          <span className="text-sm text-blue-800">AI Classify:</span>
          <TaxonomySelect value={reclassifyTaxonomy} onChange={setReclassifyTaxonomy}
            taxonomyId={reclassifyTaxonomyId} onTaxonomyIdChange={setReclassifyTaxonomyId}
            testIdPrefix="session" />
          <Button onClick={handleReclassify}
            disabled={reclassifying || (reclassifyTaxonomy === 'bespoke' && !reclassifyTaxonomyId)}
            size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="classify-button">
            {reclassifying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {reclassifying ? 'Classifying...' : 'Classify Session'}
          </Button>
        </div>
      )}

      {/* Reclassify Incomplete Records */}
      {meta.incomplete_count > 0 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-300 rounded-sm" data-testid="reclassify-incomplete-bar">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 font-medium">
            {meta.incomplete_count.toLocaleString()} records have missing or incomplete classification (L2/L3/L4)
          </span>
          <Button onClick={handleReclassifyIncomplete}
            disabled={reclassifyingIncomplete || (reclassifyTaxonomy === 'bespoke' && !reclassifyTaxonomyId)}
            size="sm" className="bg-amber-600 hover:bg-amber-700 text-white gap-1 ml-auto" data-testid="reclassify-incomplete-button">
            {reclassifyingIncomplete ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {reclassifyingIncomplete ? 'Reclassifying...' : `Fix ${meta.incomplete_count.toLocaleString()} Incomplete`}
          </Button>
        </div>
      )}

      {/* Bulk Selection Actions */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="bulk-reclassify-bar">
          <CheckSquare size={16} className="text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 font-medium">{selectedIds.size} selected</span>
          <span className="text-xs text-amber-600">|</span>
          <span className="text-sm text-amber-800">Reclassify with:</span>
          <TaxonomySelect value={bulkTaxonomy} onChange={setBulkTaxonomy}
            taxonomyId={bulkTaxonomyId} onTaxonomyIdChange={setBulkTaxonomyId}
            testIdPrefix="bulk" />
          <Button onClick={handleBulkReclassify}
            disabled={bulkReclassifying || (bulkTaxonomy === 'bespoke' && !bulkTaxonomyId)}
            size="sm" className="bg-amber-600 hover:bg-amber-700 text-white gap-1" data-testid="bulk-reclassify-button">
            {bulkReclassifying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {bulkReclassifying ? 'Reclassifying...' : 'Reclassify Selected'}
          </Button>
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs text-slate-500 hover:text-slate-700" data-testid="clear-selection-btn">
            Clear selection
          </button>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <table className="w-max min-w-full border-separate border-spacing-0 [&_th]:whitespace-nowrap" data-testid="data-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-2 py-3 text-center sticky top-0 left-0 z-30 bg-slate-50 border-b border-slate-200" style={{width: '44px', minWidth: '44px'}}>
                  <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-700" data-testid="select-all-checkbox">
                    {selectedIds.size > 0 && selectedIds.size === records.length
                      ? <CheckSquare size={15} className="text-blue-600" />
                      : <Square size={15} />}
                  </button>
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-30 bg-slate-50 border-b border-slate-200" style={{left: '44px', width: '44px', minWidth: '44px'}}>#</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-30 bg-slate-50 border-b border-slate-200" style={{left: '88px', width: '180px', minWidth: '180px'}}>Supplier</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-30 bg-slate-50 border-b border-r border-slate-200" style={{left: '268px', width: '280px', minWidth: '280px'}}>Description</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">Amount</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">Date</th>
                {extraCols.map(col => (
                  <th key={col} className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">
                    {col.replace('extra_', '').replace(/_/g, ' ')}
                  </th>
                ))}
                {hasOriginalCat && (
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">Original Cat.</th>
                )}
                {hasL2 ? (
                  <>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">L1 Category</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">L2 Sub-Cat</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">L3 Detail</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">L4 Specific</th>
                  </>
                ) : (
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">Category</th>
                )}
                {hasUnspsc && (
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">UNSPSC</th>
                )}
                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">Conf.</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">Cash</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase sticky top-0 z-20 bg-slate-50 border-b border-slate-200">Split</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map((r, idx) => {
                const conf = ((r.classification_confidence || 0) * 100);
                const rowNum = (page - 1) * perPage + idx + 1;
                const isExpanded = expandedRow === r.id;

                return (
                  <tr
                    key={r.id}
                    className={`group hover:bg-slate-50 transition-colors cursor-pointer ${selectedIds.has(r.id) ? 'bg-blue-50/50' : ''}`}
                    data-testid={`data-row-${r.id}`}
                    onClick={() => setExpandedRow(isExpanded ? null : r.id)}
                  >
                    <td className={`px-2 py-2.5 text-center sticky left-0 z-10 ${selectedIds.has(r.id) ? 'bg-blue-50' : 'bg-white group-hover:bg-slate-50'}`} onClick={e => e.stopPropagation()}>
                      <button onClick={() => toggleSelect(r.id)} className="text-slate-400 hover:text-blue-600" data-testid={`select-row-${r.id}`}>
                        {selectedIds.has(r.id) ? <CheckSquare size={15} className="text-blue-600" /> : <Square size={15} />}
                      </button>
                    </td>
                    <td className={`px-3 py-2.5 text-xs text-slate-400 sticky z-10 ${selectedIds.has(r.id) ? 'bg-blue-50' : 'bg-white group-hover:bg-slate-50'}`} style={{left: '44px', width: '44px', minWidth: '44px'}}>{rowNum}</td>
                    <td className={`px-3 py-2.5 text-sm text-slate-800 sticky z-10 ${selectedIds.has(r.id) ? 'bg-blue-50' : 'bg-white group-hover:bg-slate-50'}`} style={{left: '88px', width: '180px', maxWidth: '180px'}}>
                      <div className={isExpanded ? '' : 'truncate'}>{r.supplier}</div>
                    </td>
                    <td className={`px-3 py-2.5 text-sm text-slate-700 sticky z-10 border-r border-slate-200 ${selectedIds.has(r.id) ? 'bg-blue-50' : 'bg-white group-hover:bg-slate-50'}`} style={{left: '268px', width: '280px', minWidth: '280px', maxWidth: '280px'}}>
                      <div className={isExpanded ? '' : 'line-clamp-2'}>{r.description}</div>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-slate-800 text-right font-mono whitespace-nowrap">
                      {CURRENCY_SYMBOLS[r.currency] || r.currency || '$'}{Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{r.date}</td>
                    {extraCols.map(col => (
                      <td key={col} className="px-3 py-2.5 text-xs text-slate-600">
                        <div className={isExpanded ? '' : 'truncate'} style={{maxWidth: '120px'}}>{r[col] || '-'}</div>
                      </td>
                    ))}
                    {hasOriginalCat && (
                      <td className="px-3 py-2.5">
                        <span className="text-xs px-2 py-0.5 rounded-sm font-medium bg-amber-50 text-amber-700">
                          {r.original_custom_category || '-'}
                        </span>
                      </td>
                    )}
                    {hasL2 ? (
                      <>
                        <td className="px-3 py-2.5">
                          <span className="text-xs px-2 py-0.5 rounded-sm font-medium bg-blue-50 text-blue-700">
                            {r.category_l1 || r.category || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-700" style={{maxWidth: '180px'}}>
                          <div className={isExpanded ? '' : 'truncate'} title={r.category_l2 || ''}>{r.category_l2 || '-'}</div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-600" style={{maxWidth: '180px'}}>
                          <div className={isExpanded ? '' : 'truncate'} title={r.category_l3 || ''}>{r.category_l3 || '-'}</div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-slate-500" style={{maxWidth: '180px'}}>
                          <div className={isExpanded ? '' : 'truncate'} title={r.category_l4 || ''}>{r.category_l4 || '-'}</div>
                        </td>
                      </>
                    ) : (
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${
                          r.category ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {r.category || 'N/A'}
                        </span>
                      </td>
                    )}
                    {hasUnspsc && (
                      <td className="px-3 py-2.5 text-xs text-slate-500 font-mono">{r.unspsc_code || '-'}</td>
                    )}
                    <td className="px-3 py-2.5 text-center">
                      <span className={`text-xs font-semibold ${
                        conf >= 90 ? 'text-green-600' : conf >= 70 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {conf.toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center" data-testid={`cash-cell-${r.id}`}>
                      {r.is_cash_payment ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full font-semibold"
                          title={r.cash_indicator || 'Cash payment detected'}>
                          CASH
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {r.is_split_transaction ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full">
                          <Split size={10} /> {r.split_index}/{r.total_splits}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {records.length === 0 && (
                <tr>
                  <td colSpan={99} className="py-12 text-center text-slate-400 text-sm">
                    {recordsLoading ? 'Loading...' : searchTerm ? 'No matching records found' : 'No records available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <span className="text-xs text-slate-500">
              Showing {(page - 1) * perPage + 1}-{Math.min(page * perPage, totalRecords)} of {totalRecords}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-sm border border-slate-200 disabled:opacity-30 hover:bg-slate-100"
                data-testid="prev-page"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    className={`w-8 h-8 text-xs rounded-sm ${
                      page === p ? 'bg-[#0F172A] text-white' : 'border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-sm border border-slate-200 disabled:opacity-30 hover:bg-slate-100"
                data-testid="next-page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
